import {useReducer, useState} from 'react';

import {rfiService} from '@/services/rfiService';
import type {BusinessRFI} from '@/types';
import {BusinessRFIStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {BusinessRFIValidator} from '@/validators/BusinessRFIValidator';

interface SendRFIState {
  businessId: string;
  requestedDocuments: string[];
  description?: string;
  customerEmail?: string;
  /** Create-only: the held payment this ask is about, when there is one. */
  transactionId?: string;
  /** Draft-promotion only: an optional note sent to the business alongside the ask. */
  note?: string;
}

type SendRFIAction =
  | {
      type: 'SET_FIELD';
      field: 'description' | 'customerEmail' | 'note' | 'transactionId';
      value: string;
    }
  | {type: 'SET_DOC'; index: number; value: string}
  | {type: 'ADD_DOC'}
  | {type: 'REMOVE_DOC'; index: number};

const reducer = (state: SendRFIState, action: SendRFIAction): SendRFIState => {
  switch (action.type) {
    case 'SET_FIELD':
      return {...state, [action.field]: action.value};
    case 'SET_DOC': {
      const requestedDocuments = [...state.requestedDocuments];
      requestedDocuments[action.index] = action.value;
      return {...state, requestedDocuments};
    }
    case 'ADD_DOC':
      return {
        ...state,
        requestedDocuments: [...state.requestedDocuments, ''],
      };
    case 'REMOVE_DOC':
      return {
        ...state,
        requestedDocuments: state.requestedDocuments.filter(
          (_, i) => i !== action.index,
        ),
      };
    default:
      return state;
  }
};

/**
 * Backs the one modal that sends a document request to a business, whichever
 * way it got there.
 *
 * - No `rfi`: an admin is opening a brand new request (POST), so the full
 *   payload — businessId, description, customerEmail — is required.
 * - An `rfi` (a DRAFT): a processor already opened the request and named the
 *   payment; only the document list (and an optional note) is missing, so this
 *   is a PATCH naming them and promoting DRAFT to REQUESTED, not a new record.
 *
 * One hook rather than two because the two paths differ only in which
 * transport call they make and which fields are already filled in — the
 * document-list editing, validation shape, and post-submit reload are identical.
 */
export const useBusinessRFISend = (
  businessId: string,
  onClose: VoidFunction,
  rfi?: BusinessRFI,
) => {
  const [state, dispatch] = useReducer(reducer, {
    businessId,
    requestedDocuments: rfi?.requestedDocuments?.length
      ? rfi.requestedDocuments
      : [''],
    description: rfi?.description ?? '',
    customerEmail: rfi?.customerEmail ?? '',
    transactionId: rfi?.transactionId ?? '',
    note: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Keyed on the status, not merely on the prop being present: the only caller
  // that passes an `rfi` today is gated on Draft, but that invariant lives in a
  // different file, and reusing this modal for anything else would silently
  // send a PATCH that the server rejects.
  const isPromotingDraft = rfi?.status === BusinessRFIStatus.Draft;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (isPromotingDraft && rfi) {
        // Same rules the create path enforces, so a duplicate is caught here
        // with a readable message rather than bouncing off the server.
        const cleaned = BusinessRFIValidator.normalizeRequestedDocuments(
          state.requestedDocuments,
        );
        await rfiService.updateBusinessRFI(rfi.id, {
          requestedDocuments: cleaned,
          note: state.note?.trim() || undefined,
        });
        showSuccessMessage(
          'Documents requested; the business has been notified',
        );
      } else {
        const payload = new BusinessRFIValidator(state).value();
        await rfiService.createBusinessRFI(payload);
        showSuccessMessage('RFI created successfully');
      }
      onClose();
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      // getErrorMessage, not `(e as Error).message`: the axios interceptor
      // re-rejects the raw AxiosError, so the bare message is only ever
      // "Request failed with status code 400" and the server's actual reason
      // (duplicate document, RFI already promoted) never reaches the admin.
      showErrorMessage(
        getErrorMessage(
          e,
          isPromotingDraft
            ? 'Error requesting documents'
            : 'Error creating RFI',
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {state, dispatch, isLoading, handleSubmit, isPromotingDraft};
};

export default useBusinessRFISend;
